import { UserManager, WebStorageStateStore } from 'oidc-client';

// eslint-disable-next-line import/no-dynamic-require
const { config } = require(`../config/config.${process.env.NODE_ENV}`);

export default class AuthService {
    constructor() {
        const defaultSettings = {
            client_id: 'locate2u.appportal',
            response_type: 'id_token token',
            scope: 'openid profile locate2u.appportal',
            userStore: new WebStorageStateStore({
                store: window.localStorage
            }),
            automaticSilentRenew: true,
            accessTokenExpiringNotificationTime: 10
            // acr_values: 'pti:995e7cd9-e84f-41f0-b4fc-3917a7f239e3' // this is used for white labeling login page for the identity server
        };

        const settings = Object.assign({}, defaultSettings, config);

        this.userManager = new UserManager(settings);
        this.isLoggingOut = false;

        // When the built-in SessionMonitor detects the IS session has ended
        // (e.g. cross-portal logout), clear local state and redirect to login
        const self = this;
        this.userManager.events.addUserSignedOut(async function handleUserSignedOut() {
            if (self.isLoggingOut) return;
            const user = await self.userManager.getUser();
            if (!user) return;
            window.localStorage.clear();
            window.sessionStorage.clear();
            self.userManager.signinRedirect();
        });
    }

    async getUser() {
        const response = await this.userManager.getUser();
        return response;
    }

    async loginCallback() {
        const response = await this.userManager.signinRedirectCallback();
        return response;
    }

    async login() {
        const redirect = `${window.location.pathname}${window.location.search}`;
        const response = await this.userManager.signinRedirect({
            state: redirect
        });
        return response;
    }

    async register() {
        const response = await this.userManager.signinRedirect({
            extraQueryParams: {
                page: 'register'
            }
        });
        return response;
    }

    // Branded enterprise SSO entry (Epic CORE-6317 F9). Tag the authorize request with the
    // customer slug (acr_values=sso:<slug>) so the Identity Server routes the user to
    // /{slug}/login → their Entra sign-in. NOTE: uses the custom `sso:` prefix, not `tenant:` —
    // IdentityServer reserves and strips `tenant:`/`idp:` out of acr_values. Land on the app root
    // after sign-in (not back on /{slug}/login, which would re-trigger the redirect).
    async loginWithSso(slug) {
        const response = await this.userManager.signinRedirect({
            state: '/',
            acr_values: `sso:${slug}`
        });
        return response;
    }

    async logout() {
        this.isLoggingOut = true;

        // signoutRedirect properly terminates the IS session via
        // /connect/endsession (top-level navigation sends cookies).
        // This ensures cross-portal logout propagates correctly.
        // The _isLoggingOut flag prevents addUserSignedOut and
        // checkSessionValidity from racing with this redirect.
        await this.userManager.signoutRedirect();
    }

    async isLoggedIn() {
        const user = await this.getUser();

        return user !== null && !user.expired;
    }

    /**
     * Check if the IS session is still valid.
     * Called on tab visibility change to detect cross-portal logout.
     */
    async checkSessionValidity() {
        if (this.isLoggingOut) return;

        const user = await this.userManager.getUser();
        if (!user || user.expired) {
            return;
        }

        try {
            // Use querySessionStatus instead of signinSilent — signinSilent
            // can succeed via refresh token even when the IS session is gone.
            // querySessionStatus checks the IS session directly via iframe.
            await this.userManager.querySessionStatus();
        } catch (error) {
            // IS session expired (likely cross-portal logout) — clear and redirect
            window.localStorage.clear();
            window.sessionStorage.clear();
            await this.userManager.signinRedirect();
        }
    }

    /**
     * Set up session monitoring for cross-portal logout detection.
     * Checks IS session on tab visibility change AND on initial load.
     */
    setupSessionMonitoring() {
        const self = this;

        // Check on tab visibility change (switching back to this tab)
        document.addEventListener('visibilitychange', function handleVisibilityChange() {
            if (document.visibilityState === 'visible') {
                self.checkSessionValidity();
            }
        });

        // Check on initial load — catches cross-portal logout
        // that happened while this tab was closed or freshly opened
        this.checkSessionValidity();

        // Background check every 5 minutes — catches cross-portal logout
        // when user stays on the same tab without switching
        setInterval(function checkSession() {
            self.checkSessionValidity();
        }, 5 * 60 * 1000);
    }
}
